import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { authFetch } from '../utils/api';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';

const BASE_URL = config.BASE_URL;

const STATUS_OPTIONS = [
  { value: 'Exploitable',           label: '🔴 Exploitable',           color: '#dc2626' },
  { value: 'In Triage',             label: '🟡 In Triage',             color: '#d97706' },
  { value: 'Under Investigation',   label: '🔵 Under Investigation',   color: '#2563eb' },
  { value: 'Not Exploitable',       label: '🟢 Not Exploitable',       color: '#16a34a' },
  { value: 'Fixed',                 label: '✅ Fixed',                 color: '#059669' },
];

const JUSTIFICATION_OPTIONS = [
  'ComponentNotPresent',
  'VulnerableCodeNotPresent',
  'VulnerableCodeCannotBeControlledByAdversary',
  'InlineMitigationsAlreadyExist',
  'VulnerableCodeNotInExecutePath',
];

const SEVERITY_COLOR = { CRITICAL:'#dc2626', HIGH:'#ea580c', MEDIUM:'#d97706', LOW:'#16a34a' };

function statusBadge(status) {
  if (!status) return <span style={{ color: 'var(--bs-secondary-color)', fontSize: '0.75rem' }}>—</span>;
  const cls = {
    'Exploitable': 'open',
    'In Triage': 'in-progress',
    'Under Investigation': 'in-progress',
    'Not Exploitable': 'resolved',
    'Fixed': 'resolved',
  }[status] || 'false-pos';
  return <span className={`ce-status ce-status--${cls}`}>{status}</span>;
}

export default function VexView({ repoName, commitHash }) {
  const { t } = useTranslation();
  const [vulns, setVulns] = useState([]);
  const [vexMap, setVexMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState('');
  const [editing, setEditing] = useState(null); // vulnId being edited
  const [form, setForm] = useState({ status: 'In Triage', justification: '', detail: '', owner: '' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all'); // all | annotated | unannotated

  const load = useCallback(async () => {
    setLoading(true); setError(null); setWarning('');
    try {
      const sbomFile = commitHash.endsWith('-sbom.json') ? commitHash : `${commitHash}-sbom.json`;
      const [vRes, xRes] = await Promise.allSettled([
        authFetch(`${BASE_URL}/sbom/${repoName}/${sbomFile}/vulnerabilities`),
        authFetch(`${BASE_URL}/sbom/${repoName}/${commitHash}/vex`),
      ]);
      if (vRes.status !== 'fulfilled' || !vRes.value.ok) {
        const status = vRes.status === 'fulfilled' ? vRes.value.status : null;
        throw new Error(status ? `HTTP ${status}` : (vRes.reason?.message || 'Failed to load vulnerabilities'));
      }

      const vData = await vRes.value.json();
      let xData = [];
      if (xRes.status === 'fulfilled') {
        if (xRes.value.ok) {
          xData = await xRes.value.json();
        } else if (xRes.value.status !== 404) {
          setWarning('VEX statements could not be loaded. Showing vulnerability results without saved statements.');
        }
      } else {
        setWarning('VEX statements could not be loaded. Showing vulnerability results without saved statements.');
      }

      const vulnList = Array.isArray(vData) ? vData : (vData?.vulnerabilities || []);
      setVulns(vulnList);
      const map = {};
      (Array.isArray(xData) ? xData : []).forEach(x => { map[x.vulnId] = x; });
      setVexMap(map);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [repoName, commitHash]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (vuln) => {
    const id = vuln.vulnId || vuln.vuln_id || vuln.id;
    const existing = vexMap[id];
    setForm(existing
      ? { status: existing.status, justification: existing.justification || '', detail: existing.detail || '', owner: existing.owner || '' }
      : { status: 'In Triage', justification: '', detail: '', owner: '' });
    setEditing(id);
  };

  const save = async () => {
    setSaving(true);
    try {
      const vulnId = editing;
      await authFetch(`${BASE_URL}/sbom/${repoName}/${commitHash}/vex/${encodeURIComponent(vulnId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setEditing(null);
      await load();
    } finally { setSaving(false); }
  };

  const remove = async (vulnId) => {
    if (!window.confirm(t('vex.confirmDelete', 'VEX statement verwijderen?'))) return;
    await authFetch(`${BASE_URL}/sbom/${repoName}/${commitHash}/vex/${encodeURIComponent(vulnId)}`, { method: 'DELETE' });
    await load();
  };

  const filtered = vulns.filter(v => {
    const id = v.vulnId || v.vuln_id || v.id;
    if (filter === 'annotated') return !!vexMap[id];
    if (filter === 'unannotated') return !vexMap[id];
    if (filter === 'patchable') return !!(v.fixedIn || v.fixed_in);
    return true;
  });

  const stats = {
    total: vulns.length,
    annotated: Object.keys(vexMap).length,
    exploitable: Object.values(vexMap).filter(x => x.status === 'Exploitable').length,
    fixed: Object.values(vexMap).filter(x => x.status === 'Fixed').length,
    notExploitable: Object.values(vexMap).filter(x => x.status === 'Not Exploitable').length,
    patchable: vulns.filter(v => !!(v.fixedIn || v.fixed_in)).length,
  };

  if (loading) return (
    <div style={{ padding: '1rem 0' }}>
      <Skeleton.Table rows={5} cols={5} />
    </div>
  );
  if (error) return <div className="alert alert-danger">{t('vex.loadError', 'Laden mislukt')}: {error}</div>;

  return (
    <div>
      {warning && <div className="alert alert-warning">{warning}</div>}
      {/* Stats bar */}
      <div className="ce-kpi-row" style={{ marginBottom: '1.25rem' }}>
        {[
          { label: t('vex.totalCves', 'Totaal CVEs'), value: stats.total, color: '#6366f1' },
          { label: t('vex.annotated', 'Geannoteerd'), value: `${stats.annotated}/${stats.total}`, color: '#0891b2' },
          { label: t('vex.exploitable', 'Exploitable'), value: stats.exploitable, color: stats.exploitable > 0 ? '#dc2626' : '#16a34a' },
          { label: t('vex.notExploitable', 'Not Exploitable'), value: stats.notExploitable, color: '#16a34a' },
          { label: t('vex.fixed', 'Fixed'), value: stats.fixed, color: '#059669' },
          { label: t('vex.patchable', 'Patch beschikbaar'), value: stats.patchable, color: stats.patchable > 0 ? '#6366f1' : '#6b7280' },
        ].map(s => (
          <div key={s.label} className="ce-kpi" style={{ '--kpi-color': s.color }}>
            <div className="ce-kpi-value">{s.value}</div>
            <div className="ce-kpi-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="ce-filter-bar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>{t('vex.filterLabel', 'Filter')}:</span>
        {['all', 'annotated', 'unannotated', 'patchable'].map(f => (
          <button key={f} className={`ce-btn ce-btn--sm ${filter === f ? 'ce-btn--primary' : 'ce-btn--secondary'}`} onClick={() => setFilter(f)}>
            {f === 'all' ? t('vex.filterAll', 'Alle')
              : f === 'annotated' ? t('vex.filterAnnotated', 'Geannoteerd')
              : f === 'patchable' ? `🔧 ${t('vex.filterPatchable', 'Patch beschikbaar')}`
              : t('vex.filterUnannotated', 'Zonder statement')}
          </button>
        ))}
        <span className="ce-filter-count">{filtered.length} {t('vex.results', 'resultaten')}</span>
      </div>

      {/* CVE list */}
      {filtered.length === 0 ? (
        <EmptyState
          icon="🔏"
          title={t('vex.emptyTitle')}
          description={t('vex.emptyDescription')}
          size="sm"
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {filtered.map(v => {
            const vulnId = v.vulnId || v.vuln_id || v.id;
            const vex = vexMap[vulnId];
            const isEditing = editing === vulnId;
            const pkgName = v.packageName || v.package_name || '';
            const cvssScore = v.cvssScore ?? v.cvss_score ?? 0;
            return (
              <div key={vulnId} className="ce-card" style={{ marginBottom: '0.5rem' }}>
                <div className="ce-card-body" style={{ padding: '0.875rem 1rem' }}>
                <div className="d-flex align-items-start gap-3 flex-wrap">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                      <code style={{ fontSize: '0.8125rem', fontWeight: 700 }}>{vulnId}</code>
                      <span className={`ce-sev ce-sev--${(v.severity || 'none').toLowerCase()}`}>{v.severity || '?'}</span>
                      {cvssScore > 0 && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>CVSS {cvssScore.toFixed(1)}</span>
                      )}
                      {statusBadge(vex?.status)}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', marginBottom: '0.25rem' }}>
                      {pkgName}{v.version ? `@${v.version}` : ''}
                      {(v.fixedIn || v.fixed_in) && (
                        <span className="ce-sev ce-sev--low" style={{ marginLeft: '0.5rem' }}>🔧 → {v.fixedIn || v.fixed_in}</span>
                      )}
                    </div>
                    {v.summary && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--bs-body-color)', lineHeight: 1.5 }}>{v.summary}</div>
                    )}
                    {vex && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--bs-secondary-color)' }}>
                        {vex.justification && <span>📌 {vex.justification} · </span>}
                        {vex.detail && <span>{vex.detail} · </span>}
                        {vex.owner && <span>👤 {vex.owner} · </span>}
                        <span>{new Date(vex.updatedAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-1 flex-shrink-0">
                    <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => isEditing ? setEditing(null) : startEdit(v)}>
                      {isEditing ? '✕' : (vex ? '✏️' : '+ VEX')}
                    </button>
                    {vex && !isEditing && (
                      <button className="ce-btn ce-btn--danger ce-btn--sm" onClick={() => remove(vulnId)}>🗑️</button>
                    )}
                  </div>
                </div>

                {/* Inline edit form */}
                {isEditing && (
                  <div style={{
                    marginTop: '0.875rem',
                    paddingTop: '0.875rem',
                    borderTop: '1px solid var(--bs-border-color)',
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem',
                  }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
                        {t('vex.status', 'Status')} *
                      </label>
                      <select className="form-select form-select-sm" value={form.status}
                        onChange={e => setForm(f => ({ ...f, status: e.target.value, justification: e.target.value !== 'Not Exploitable' ? '' : f.justification }))}>
                        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    {form.status === 'Not Exploitable' && (
                      <div>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
                          {t('vex.justification', 'Onderbouwing')}
                        </label>
                        <select className="form-select form-select-sm" value={form.justification}
                          onChange={e => setForm(f => ({ ...f, justification: e.target.value }))}>
                          <option value="">— kies —</option>
                          {JUSTIFICATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    )}
                    <div style={{ gridColumn: form.status === 'Not Exploitable' ? '1 / -1' : undefined }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
                        {t('vex.detail', 'Toelichting')}
                      </label>
                      <input className="form-control form-control-sm" placeholder={t('vex.detailPlaceholder', 'Optionele toelichting…')}
                        value={form.detail} onChange={e => setForm(f => ({ ...f, detail: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
                        {t('vex.owner', 'Eigenaar')}
                      </label>
                      <input className="form-control form-control-sm" placeholder="naam of team"
                        value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} />
                    </div>
                    <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => setEditing(null)}>
                        {t('common.cancel', 'Annuleren')}
                      </button>
                      <button className="ce-btn ce-btn--primary ce-btn--sm" onClick={save} disabled={saving}>
                        {saving ? '⏳' : t('vex.save', 'Statement opslaan')}
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
